import { useState } from "react";
import axios from "axios";
import { Heart, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "../context/ContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RSVP = () => {
  const { content } = useContent();
  const [form, setForm] = useState({
    name: "",
    email: "",
    attending: "ja",
    guests: 0,
    dietary: "",
    hotel: "nein",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Bitte gib deinen Namen an");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: form.email.trim() || null,
        guests: Number(form.guests) || 0,
      };
      await axios.post(`${API}/rsvp`, payload);
      setSent(true);
      toast.success("Deine Zusage wurde übermittelt – danke!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Etwas ist schief gelaufen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="rsvp"
      data-testid="section-rsvp"
      className="py-24 md:py-36 bg-sage-800 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-15 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://cloud.nhaage.de/index.php/apps/files_sharing/publicpreview/dxs2C3AMqtFKr5D?file=/20260427-201441_Nico_IMG_0521.jpeg&x=1600&y=1600&a=1')",
        }}
      />
      <div className="absolute inset-0 grain" />

      <div className="container mx-auto px-6 md:px-12 relative">
        <div className="max-w-2xl mx-auto text-center mb-14">
          <p className="text-sage-200 uppercase tracking-[0.35em] text-xs mb-6">
            {content.rsvp_eyebrow}
          </p>
          <h2
            data-testid="rsvp-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white"
          >
            {content.rsvp_title_prefix}{" "}
            <span className="font-script text-sage-200 text-5xl sm:text-6xl lg:text-7xl">
              {content.rsvp_title_script}
            </span>
          </h2>
          <p className="mt-6 text-sage-100/90 text-base md:text-lg">
            {(content.rsvp_description || "").replace("{deadline}", content.rsvp_deadline || "")}
          </p>
        </div>

        {sent ? (
          <div
            data-testid="rsvp-success"
            className="max-w-xl mx-auto bg-white/95 backdrop-blur border border-sage-200 rounded-2xl p-10 text-center shadow-xl"
          >
            <CheckCircle2 className="mx-auto text-sage-500 mb-4" size={48} />
            <h3 className="font-serif text-3xl text-sage-800 mb-3">Vielen Dank!</h3>
            <p className="text-sage-700">
              Deine Nachricht ist bei uns angekommen. Wir freuen uns auf dich!
            </p>
            <button
              data-testid="rsvp-send-another"
              onClick={() => {
                setSent(false);
                setForm({ name: "", email: "", attending: "ja", guests: 0, dietary: "", hotel: "nein", message: "" });
              }}
              className="mt-6 text-sage-600 hover:text-sage-800 text-sm underline"
            >
              Noch eine Zusage senden
            </button>
          </div>
        ) : (
          <form
            data-testid="rsvp-form"
            onSubmit={submit}
            className="max-w-2xl mx-auto bg-white/95 backdrop-blur border border-sage-200 rounded-2xl p-6 md:p-10 shadow-xl space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name *">
                <input
                  data-testid="rsvp-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className={inputCls}
                  placeholder="Max Mustermann"
                />
              </Field>
              <Field label="E-Mail (optional)">
                <input
                  data-testid="rsvp-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className={inputCls}
                  placeholder="max@beispiel.de"
                />
              </Field>
            </div>

            <Field label="Kommst du?">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: "ja", label: "Ja, ich komme gern", testid: "rsvp-attending-yes" },
                  { v: "nein", label: "Leider nicht", testid: "rsvp-attending-no" },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.v}
                    data-testid={opt.testid}
                    onClick={() => update("attending", opt.v)}
                    className={`rounded-xl border-2 px-4 py-4 text-sm transition-all ${
                      form.attending === opt.v
                        ? "border-sage-500 bg-sage-50 text-sage-800"
                        : "border-sage-200 bg-white text-sage-600 hover:border-sage-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </Field>

            {form.attending === "ja" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Anzahl Begleitpersonen">
                    <input
                      data-testid="rsvp-guests"
                      type="number"
                      min="0"
                      max="10"
                      value={form.guests}
                      onChange={(e) => update("guests", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Ernährungswünsche / Allergien">
                    <input
                      data-testid="rsvp-dietary"
                      type="text"
                      value={form.dietary}
                      onChange={(e) => update("dietary", e.target.value)}
                      className={inputCls}
                      placeholder="z.B. vegetarisch, Nussallergie"
                    />
                  </Field>
                </div>

                <Field label="Übernachtung im Gut Haferkorn?">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { v: "ja", label: "Ja, bitte Zimmer", testid: "rsvp-hotel-yes" },
                      { v: "vielleicht", label: "Vielleicht", testid: "rsvp-hotel-maybe" },
                      { v: "nein", label: "Nein, danke", testid: "rsvp-hotel-no" },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.v}
                        data-testid={opt.testid}
                        onClick={() => update("hotel", opt.v)}
                        className={`rounded-xl border-2 px-4 py-3 text-sm transition-all ${
                          form.hotel === opt.v
                            ? "border-sage-500 bg-sage-50 text-sage-800"
                            : "border-sage-200 bg-white text-sage-600 hover:border-sage-300"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-sage-500">
                    Das Gut Haferkorn bietet 27 Zimmer direkt vor Ort · EZ ab 86&nbsp;€ · DZ ab 138&nbsp;€ (inkl. Frühstück). Wir reservieren gerne für euch.
                  </p>
                </Field>
              </>
            )}

            <Field label="Nachricht ans Brautpaar">
              <textarea
                data-testid="rsvp-message"
                rows={4}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className={inputCls + " resize-none"}
                placeholder="Eine Glückwunsch-Zeile, eine Anekdote, ein Liedwunsch..."
              />
            </Field>

            <button
              data-testid="rsvp-submit"
              type="submit"
              disabled={submitting}
              className="w-full rounded-full bg-sage-500 hover:bg-sage-600 disabled:bg-sage-300 text-white px-8 py-4 text-sm uppercase tracking-[0.25em] transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              <Heart size={16} />
              {submitting ? "Wird gesendet..." : "Zusage absenden"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

const inputCls =
  "w-full rounded-lg border border-sage-300 bg-white px-4 py-3 text-sm text-sage-800 placeholder:text-sage-400 focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-sage-400";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-sage-600 mb-2">
      {label}
    </span>
    {children}
  </label>
);

export default RSVP;
